import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Eye, EyeOff, Copy, Check, Clock, Send, ArrowDownLeft } from 'lucide-react';
import { walletAPI } from '../services/api';
import { formatCurrency, CRYPTO_SYMBOLS, CRYPTO_NAMES, getCurrencyDisplay } from '../utils/constants';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

const SUPPORTED_COINS = [
  { id: 'BTC',  name: 'Bitcoin',    network: 'Bitcoin Network',  networkTag: 'BTC' },
  { id: 'ETH',  name: 'Ethereum',   network: 'Ethereum',         networkTag: 'ERC20' },
  { id: 'USDT', name: 'Tether USD', network: 'Tron Network',     networkTag: 'TRC20' },
  { id: 'USDC', name: 'USD Coin',   network: 'Base Network',     networkTag: 'BASE' },
  { id: 'SOL',  name: 'Solana',     network: 'Solana',           networkTag: 'SOL' },
  { id: 'TON',  name: 'Toncoin',    network: 'TON Network',      networkTag: 'TON' },
];
const displayedCryptos = new Set(SUPPORTED_COINS.map(c => c.id));

const Wallet = () => {
  const navigate = useNavigate();
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBalances, setShowBalances] = useState(true);
  const [depositOpen, setDepositOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [currencyDisplay, setCurrencyDisplay] = useState(getCurrencyDisplay());

  useEffect(() => {
    loadBalances();

    const handleCurrencyChange = (e) => setCurrencyDisplay(e.detail);
    window.addEventListener('currencyDisplayChanged', handleCurrencyChange);
    return () => window.removeEventListener('currencyDisplayChanged', handleCurrencyChange);
  }, []);

  const loadBalances = async () => {
    try {
      const response = await walletAPI.getBalances();
      setBalances(response.data.filter(b => b.crypto !== 'AED' && displayedCryptos.has(b.crypto)));
    } catch (error) {
      toast.error('Failed to load balances');
    } finally {
      setLoading(false);
    }
  };

  const totalValue = balances.reduce((sum, b) => sum + b.value_aed, 0);

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-primary font-bold text-white mb-2">WALLET</h1>
            <p className="text-gray-400 font-secondary">Manage your crypto assets</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowBalances(!showBalances)}
              className="p-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-all duration-300"
              data-testid="toggle-balance-visibility-btn"
            >
              {showBalances ? <EyeOff className="w-5 h-5 text-white" /> : <Eye className="w-5 h-5 text-white" />}
            </button>
            <Button
              onClick={() => navigate('/send')}
              className="bg-white/10 hover:bg-white/20 text-white rounded-full px-4 border border-white/10"
            >
              <Send className="w-4 h-4 mr-2" />
              Send
            </Button>
            <Dialog open={depositOpen} onOpenChange={setDepositOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#7000FF] hover:bg-[#5B00CC] text-white rounded-full px-6 neon-glow-primary" data-testid="open-deposit-dialog-btn">
                  <Plus className="w-5 h-5 mr-2" />
                  Receive
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A051E] border-white/10 text-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-primary text-2xl">RECEIVE CRYPTO</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 mt-4">
                  {/* Info banner */}
                  <div className="bg-[#7000FF]/10 border border-[#7000FF]/30 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <ArrowDownLeft className="w-5 h-5 text-[#7000FF]" />
                      <p className="text-[#7000FF] font-semibold text-sm">Embedded Wallet Deposit</p>
                    </div>
                    <p className="text-gray-300 text-xs">
                      Your wallet addresses are generated by Dynamic. Send crypto to the addresses below to fund your wallet.
                    </p>
                  </div>

                  {/* Wallet addresses — will be populated in Phase 4 */}
                  <div className="space-y-3">
                    {SUPPORTED_COINS.map(coin => (
                      <div key={coin.id} className="bg-white/5 border border-white/10 rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#7000FF]/20 flex items-center justify-center">
                              <span className="text-xl">{CRYPTO_SYMBOLS[coin.id]}</span>
                            </div>
                            <div>
                              <p className="text-white font-semibold text-sm">{coin.name}</p>
                              <span className="text-xs bg-white/10 px-2 py-0.5 rounded font-mono text-[#00F0FF]">
                                {coin.networkTag}
                              </span>
                            </div>
                          </div>
                          <p className="text-gray-500 text-xs italic">Coming soon</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="text-gray-500 text-xs text-center">
                    Real deposit addresses will appear once wallet provisioning is complete.
                  </p>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Total Value Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="glass-card p-8 rounded-2xl"
          data-testid="total-crypto-value-card"
        >
          <span className="text-gray-400 font-secondary text-sm uppercase tracking-wider">
            Total Portfolio Value {currencyDisplay === 'CRYPTO' ? '(in BTC)' : '(in AED)'}
          </span>
          <div className="text-5xl font-mono font-bold text-white mt-4">
            {showBalances ? (
              currencyDisplay === 'CRYPTO'
                ? `${(totalValue / 220000).toFixed(6)} BTC`
                : formatCurrency(totalValue)
            ) : '•••••'}
          </div>
          {currencyDisplay === 'CRYPTO' && showBalances && (
            <p className="text-gray-400 text-sm mt-2">≈ {formatCurrency(totalValue)}</p>
          )}
        </motion.div>

        {/* Balances Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-full text-center text-gray-400 py-12">Loading...</div>
          ) : (
            balances.map((balance, index) => (
              <motion.div
                key={balance.crypto}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card p-6 rounded-2xl hover:border-[#7000FF]/50 transition-all duration-300"
                data-testid={`crypto-balance-${balance.crypto.toLowerCase()}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#7000FF]/20 flex items-center justify-center">
                      <span className="text-2xl">{CRYPTO_SYMBOLS[balance.crypto]}</span>
                    </div>
                    <div>
                      <div className="text-white font-secondary font-semibold">{CRYPTO_NAMES[balance.crypto]}</div>
                      <div className="text-gray-400 text-sm font-mono">{balance.crypto}</div>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">
                      {currencyDisplay === 'CRYPTO' ? 'Crypto Balance' : 'Balance'}
                    </div>
                    <div className={`text-2xl font-mono font-bold ${currencyDisplay === 'CRYPTO' ? 'text-[#FFD700]' : 'text-white'}`}>
                      {showBalances ? formatCurrency(balance.balance, balance.crypto) : '•••••'}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-xs uppercase tracking-wider mb-1">Value (AED)</div>
                    <div className={`text-lg font-mono ${currencyDisplay === 'CRYPTO' ? 'text-gray-400' : 'text-[#00F0FF]'}`}>
                      {showBalances ? formatCurrency(balance.value_aed) : '•••••'}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Wallet;
